import { ENTITY_TYPES_COMMENTS } from '@constants';
import { getCleanedUrlParams, request } from '@utils';
import { SUBSCRIPTION_HISTORY_CHANGES_DEFAULT_PAGE_SIZE } from 'hooks/subscriptions/constants';
import { omit } from 'lodash-es';

const getSubscriptions = async ({ params, payload }) => {
  const body = {
    body: payload,
  };

  const results = await request.post(
    `/v1/subscriptions/filter?${new URLSearchParams(params)}`,
    body
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getContracts = async ({ params, payload }) => {
  const body = {
    body: payload,
  };

  const results = await request.post(
    `/v1/contracts/filter?${new URLSearchParams(params)}`,
    body
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionsEnums = async () => {
  const results = await request.get(`/v1/subscriptions/enums`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionById = async (id) => {
  const results = await request.get(`/v1/subscriptions/${id}`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionLinkedItems = async (
  subscriptionId,
  { page = 1, pageSize = 500, order = 'desc' } = {}
) => {
  const results = await request.get(
    `/v1/subscriptions/${subscriptionId}/linked-items?${new URLSearchParams({
      page,
      pageSize,
      order,
    })}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const editSubscriptionById = async (subscriptionID, payload) => {
  const params = {
    body: payload,
  };
  const results = await request.patch(
    `/v1/subscriptions/${subscriptionID}`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const editSubscriptionRuleById = async (subscriptionID, field, payload) => {
  const params = {
    body: payload,
  };
  const results = await request.patch(
    `/v1/subscriptions/${subscriptionID}/rules/${field}`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const editSubscriberRole = async (subscriptionID, subscriberId, payload) => {
  // we use it
  const params = {
    body: payload,
  };
  const results = await request.patch(
    `/v1/subscriptions/${subscriptionID}/subscribers/${subscriberId}/role`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addFee = async (subscriptionID, ruleId, payload) => {
  const params = {
    body: payload,
  };
  const results = await request.post(
    `/v1/subscriptions/${subscriptionID}/rules/${ruleId}/fees`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addDriver = async (subscriptionID, body) => {
  const params = {
    body,
  };
  const results = await request.post(
    `/v1/subscriptions/${subscriptionID}/subscribers`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const editSubscriber = async (subscriptionID, subscriberId, payload) => {
  const params = {
    body: payload,
  };
  const results = await request.patch(
    `/v1/subscriptions/${subscriptionID}/subscribers/${subscriberId}`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const requestDrivers = async (subscriptionID) => {
  const params = {
    body: null,
  };
  const results = await request.post(
    `/v1/subscriptions/${subscriptionID}/add-driver-mail`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const removeDriver = async (subscriptionId, userId) => {
  const results = await request.remove(
    `/v1/users/${userId}/subscription/${subscriptionId}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const cancelHandover = async (subscriptionID, type) => {
  const params = {
    body: null,
  };
  const results = await request.post(
    `/v1/subscriptions/${subscriptionID}/handover/${type}/cancel`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const cancelInspection = async (subscriptionID, type) => {
  const params = {
    body: null,
  };
  const results = await request.patch(
    `/v1/subscriptions/${subscriptionID}/inspection/${type}/cancel`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionChangeLogHistoryById = async (id) => {
  const results = await request.get(
    `/v1/changelogs?entityType=subscription&entityID=${id}&orderBy=createdAt&order=desc`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionEmailHistoryById = async (id) => {
  const payload = {
    body: {},
  };

  const results = await request.post(
    `/v1/emails/history?entityType=subscription&entityID=${id}&page=1&pageSize=500&orderBy=createdAt&order=desc`,
    payload
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addDocument = async (id, formData) => {
  const payload = {
    body: formData,
    header: {
      Accept: '*/*',
    },
  };

  const results = await request.post(`/v1/subscriptions/${id}/docs`, payload);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const exportSubscriptions = async ({ params, rule }) => {
  const cleanParams = omit(params, ['page', 'pageSize']);

  const body = {
    body: { type: 'csv', rule },
  };

  const results = await request.post(
    `/v1/subscriptions/export/async?${new URLSearchParams({
      ...cleanParams,
    })}`,
    body
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const exportSubscriptionsBundles = async ({ params, rule }) => {
  const cleanParams = omit(params, ['page', 'pageSize']);

  const body = {
    body: { type: 'csv', rule },
  };

  const results = await request.post(
    `/v1/subscriptions/bundles/export/async?${new URLSearchParams({
      ...cleanParams,
    })}`,
    body
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const editSubscriptionFee = async (subscriptionId, feeId, values) => {
  const params = {
    body: values,
  };
  const results = await request.patch(
    `/v1/subscriptions/${subscriptionId}/fees/${feeId}`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const deleteSubscriptionFee = async (subscriptionId, feeId) => {
  const results = await request.remove(
    `/v1/subscriptions/${subscriptionId}/fees/${feeId}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const generateHandoverPdf = async (subscriptionId, type) => {
  const results = await request.post(
    `/v1/subscriptions/${subscriptionId}/handover/${type}/pdf/generation`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionBillingInfoByTin = async ({ subscriptionId, tin }) => {
  const results = await request.get(
    `/v1/subscriptions/${subscriptionId}/billingInfo/${tin}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionContractsTemplates = async () => {
  const results = await request.get(`/v1/contracts/templates`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addContract = async (payload) => {
  const params = {
    body: payload,
  };
  const results = await request.post(`/v1/contracts`, params);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionContracts = async (subscriptionId) => {
  const results = await request.get(
    `/v1/contracts?subscriptionID=${subscriptionId}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const exportContractsFileAsync = async ({ params, payload }) => {
  const body = {
    body: { rule: payload, type: 'csv' },
  };

  const results = await request.post(
    `/v1/contracts/export?${new URLSearchParams({
      ...params,
    })}`,
    body
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const resetHandoverMethod = async (subscriptionId, type) => {
  const payload = {
    body: null,
  };

  const results = await request.patch(
    `/v1/subscriptions/${subscriptionId}/handover/${type}/resetMethod`,
    payload
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const sendSubscriptionSms = async (id, payload) => {
  const params = {
    body: payload,
  };
  const results = await request.post(`/v1/subscriptions/${id}/sms`, params);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const sendKeyDeliveredEmail = async (subscriptionId) => {
  const payload = {
    body: null,
  };

  const results = await request.post(
    `/v1/subscriptions/${subscriptionId}/delivery/sendKeyDeliveredEmail`,
    payload
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionBundles = async (subscriptionId) => {
  const results = await request.get(
    `/v1/subscriptions/${subscriptionId}/bundles`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionBundlesConfig = async (subscriptionId) => {
  const results = await request.get(
    `/v1/subscriptions/${subscriptionId}/bundles/config`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addSubscriptionBundle = async (subscriptionId, payload) => {
  const params = {
    body: payload,
  };

  const results = await request.post(
    `/v1/subscriptions/${subscriptionId}/bundles`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addGuarantorAgreement = async (subscriptionId, payload) => {
  const params = {
    body: payload,
  };
  const results = await request.post(
    `/v1/subscriptions/${subscriptionId}/guarantor`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const updateGuarantorAgreement = async (
  subscriptionId,
  agreementId,
  payload
) => {
  const params = {
    body: payload,
  };
  const results = await request.patch(
    `/v1/subscriptions/${subscriptionId}/guarantor/${agreementId}`,
    params
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const deleteGuarantorAgreement = async (subscriptionId, agreementId) => {
  const results = await request.remove(
    `/v1/subscriptions/${subscriptionId}/guarantor/${agreementId}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionComments = async (subscriptionId) => {
  const results = await request.get(
    `/v1/comments?entityType=${ENTITY_TYPES_COMMENTS.subscriptions}&entityID=${subscriptionId}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const addSubscriptionComment = async (payload) => {
  const params = {
    body: payload,
  };

  const results = await request.post(`/v1/comments`, params);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const deleteSubscriptionComment = async (commentId) => {
  const results = await request.remove(`/v1/comments/${commentId}`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionHistoryChanges = async ({
  subscriptionId,
  page = 1,
  pageSize = SUBSCRIPTION_HISTORY_CHANGES_DEFAULT_PAGE_SIZE,
  order = 'desc',
  orderBy = 'createdAt',
}) => {
  const params = {
    page,
    pageSize,
    order,
    orderBy,
  };

  const cleanParams = getCleanedUrlParams(params);

  const results = await request.get(
    `/v1/subscriptions/${subscriptionId}/history?${new URLSearchParams(
      cleanParams
    )}`
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionSms = async ({ params, payload }) => {
  const body = {
    body: payload,
  };

  const results = await request.post(
    `/v1/communications/filter?${new URLSearchParams(params)}`,
    body
  );

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};

const getSubscriptionContractsEnums = async () => {
  const results = await request.get(`/v1/contracts/enums`);

  if (results?.error) {
    return Promise.reject(results?.message);
  }

  return results;
};
export {
  getSubscriptions,
  getSubscriptionById,
  getSubscriptionLinkedItems,
  getContracts,
  editSubscriptionRuleById,
  editSubscriptionById,
  editSubscriberRole,
  addFee,
  addDriver,
  getSubscriptionsEnums,
  requestDrivers,
  cancelHandover,
  getSubscriptionChangeLogHistoryById,
  getSubscriptionEmailHistoryById,
  addDocument,
  exportSubscriptions,
  exportSubscriptionsBundles,
  removeDriver,
  editSubscriptionFee,
  deleteSubscriptionFee,
  generateHandoverPdf,
  cancelInspection,
  getSubscriptionBillingInfoByTin,
  editSubscriber,
  // contracts
  getSubscriptionContractsTemplates,
  addContract,
  getSubscriptionContracts,
  exportContractsFileAsync,
  resetHandoverMethod,
  sendSubscriptionSms,
  sendKeyDeliveredEmail,
  getSubscriptionBundles,
  getSubscriptionBundlesConfig,
  addSubscriptionBundle,
  addGuarantorAgreement,
  updateGuarantorAgreement,
  deleteGuarantorAgreement,
  getSubscriptionComments,
  addSubscriptionComment,
  deleteSubscriptionComment,
  getSubscriptionHistoryChanges,
  getSubscriptionSms,
  getSubscriptionContractsEnums,
};
